import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { HttpClientModule } from '@angular/common/http';
import { AuthService } from '../../core/auth/auth.service';
import { TranslocoModule } from '@ngneat/transloco';

@Component({
    selector: 'app-reset-password',
    standalone: true,
    imports: [
        RouterLink,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        ReactiveFormsModule,
        HttpClientModule,
        TranslocoModule,
    ],
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {
    // isToggled
    isToggled = false;

    // Password Hide
    hide = true;
    hide2 = true;

    // Form
    authForm: FormGroup;

    // Show alert
    showAlert: boolean = false;

    // token
    token: string;

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        public themeService: CustomizerSettingsService,
        private authService: AuthService
    ) {
        this.authForm = this.fb.group(
            {
                password: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(8),
                        Validators.maxLength(50),
                        Validators.pattern(
                            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!-\/:-@[-`{-~]).{8,50}/
                        ),
                    ],
                ],
                confirmPassword: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(8),
                        Validators.maxLength(50),
                        Validators.pattern(
                            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!-\/:-@[-`{-~]).{8,50}/
                        ),
                    ],
                ],
            },
            {
                validator: this.matchValidator('password', 'confirmPassword'),
            }
        );
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // Dark Mode
    toggleTheme() {
        this.themeService.toggleTheme();
    }

    // Card Border
    toggleCardBorderTheme() {
        this.themeService.toggleCardBorderTheme();
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    // ngOnInit
    ngOnInit() {
        this.token = this.activatedRoute.snapshot.queryParamMap.get('token') || '';
    }

    // Match Validator
    matchValidator(
        controlName: string,
        matchingControlName: string
    ): ValidatorFn {
        return (abstractControl: AbstractControl) => {
            const control = abstractControl.get(controlName);
            const matchingControl = abstractControl.get(matchingControlName);

            if (
                matchingControl!.errors &&
                !matchingControl!.errors?.['confirmedValidator']
            ) {
                return null;
            }

            if (control!.value !== matchingControl!.value) {
                const error = { confirmedValidator: 'Passwords do not match.' };
                matchingControl!.setErrors(error);
                return error;
            } else {
                matchingControl!.setErrors(null);
                return null;
            }
        };
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.authForm.invalid) {
            this.showAlert = true;
            return;
        }

        // Disable the form
        this.authForm.disable();

        // Hide the alert
        this.showAlert = false;

        // Sign in
        this.authService
            .resetPassword(this.token, this.authForm.get('password')?.value)
            .subscribe(
                () => {
                    // Set the redirect url.
                    // The '/signed-in-redirect' is a dummy url to catch the request and redirect the user
                    // to the correct page after a successful sign in. This way, that url can be set via
                    // routing file and we don't have to touch here.
                    const redirectURL =
                        this.activatedRoute.snapshot.queryParamMap.get(
                            'redirectURL'
                        ) || '/signed-in-redirect';

                    // Navigate to the redirect url
                    this.router.navigateByUrl(redirectURL);
                },
                (response) => {
                    // Re-enable the form
                    this.authForm.enable();

                    // Show the alert
                    this.showAlert = true;
                }
            );
    }
}
